import React, { useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import CareersProfileForm, {
	ICareersProfileModalI18n,
} from '../CareersProfileForm';

export interface ICareersProfileCardI18n {
	editLinkLabel: string;
	resumeLinkLabel: string;
	resumeLinkHelperText: string;
}

export interface ICareersProfileCardProps extends IBaseElementProps {
	/**
	 * Profile Image - Recommended size: 80px x 80px
	 */
	profileImage?: IImage;
	/**
	 * Profile Name
	 */
	profileName?: string;
	/**
	 * Profile Title
	 */
	profileTitle?: string;
	editable?: boolean;
	modalI18n?: ICareersProfileModalI18n;
	i18n?: ICareersProfileCardI18n;
}

const CareersProfileCard: React.FC<ICareersProfileCardProps> = ({
	id,
	className,
	classNames = [],
	profileImage,
	profileName,
	profileTitle,
	editable = true,
	modalI18n,
	i18n = {
		editLinkLabel: 'Edit my profile',
		resumeLinkLabel: 'Upload my resume',
		resumeLinkHelperText: 'Upload my resume',
	},
}) => {
	const [showModal, setShowModal] = useState<boolean>(false);
	const [formFields, setFormFields] = useState<string[]>(null);

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-career-profile-card card d-flex flex-row flex-lg-column justify-content-between pt-1 pb-3 p-sm-3 px-lg-5 pt-lg-6 pb-lg-5 border-0',
					...classNames,
				)
			}
		>
			<div className="card-header p-0 bg-transparent border-0">
				<div className="d-flex flex-row justify-content-start align-items-center">
					{profileImage && (
						<div className="card-header__profile-image me-1 me-md-3 pe-1">
							<Image {...profileImage} className="img-fluid" />
						</div>
					)}
					<div className="card-header__profile-info pt-1">
						{profileName && (
							<h4 className="card-header__profile-name fs-18px fs-md-4 mb-0">
								{profileName}
								<span
									className="ms-1 ms-sm-2 ps-1 fs-7 fs-sm-4 position-absolute"
									role="img"
									aria-label="Waving Hand Emoji"
								>
									👋
								</span>
							</h4>
						)}
						{(profileTitle || i18n.editLinkLabel) && (
							<p className="card-header__profile-title text-mid-grey mb-0 fs-8 fs-lg-7 lh-sm lh-lg-base">
								{profileTitle}

								{(editable && i18n.editLinkLabel) && (
									<Button
										onClick={() => {
											setShowModal(true);
											setFormFields([
												'first_name',
												'last_name',
												'job_title',
												'profile_picture',
											]);
										}}
										aria-label={i18n.editLinkLabel}
										className="card-header__profile-edit-link fs-7 link-mid-grey text-decoration-none bg-transparent border-0"
									>
										<span className="icon icon-pencil ms-1"></span>
									</Button>
								)}
							</p>
						)}
					</div>
				</div>
			</div>

			{(editable && i18n.resumeLinkLabel) && (
				<div className="card-body d-flex flex-row justify-content-end justify-content-lg-start align-items-center mt-lg-4 p-0 border-0">
					<>
						<Button
							onClick={() => {
								setShowModal(true);
								setFormFields(['resume']);
							}}
							label={i18n.resumeLinkLabel}
							className="btn btn-sm btn-dark d-none d-sm-inline"
						>
							<span className="icon icon-upload ms-2"></span>
						</Button>
						{i18n.resumeLinkHelperText && (
							<div className="d-flex justify-content-end d-sm-none">
								<span className="resume-link-helper-text w-50 ms-1 pt-1 pe-1 fs-9 lh-sm text-mid-grey text-end">
									{i18n.resumeLinkHelperText}
								</span>
								<Button
									className="resume-link-mobile-button btn btn-sm btn-dark d-flex justify-content-center align-items-center p-0"
									onClick={() => {
										setShowModal(true);
										setFormFields(['resume']);
									}}
									aria-label={i18n.resumeLinkLabel}
								>
									<span className="resume-link-icon icon icon-upload ms-sm-2"></span>
								</Button>
							</div>
						)}
					</>
				</div>
			)}

			{showModal && (
				<CareersProfileForm
					fields={formFields}
					onCancel={() => setShowModal(false)}
					modalI18n={modalI18n}
				/>
			)}
		</div>
	);
};

export default CareersProfileCard;
